<template>
    <div class="app-hub-page field-center">
        <h1 class="app-hub-page-title">{{ pageTitle }}</h1>
        <el-card class="field-center__shell app-hub-card" shadow="never">
            <el-tabs v-model="activeTab" class="field-center__tabs app-hub-tabs" type="border-card">
                <el-tab-pane label="外勤订单" name="orders">
                    <div class="field-center__pane app-hub-pane">
                        <el-row :gutter="12" class="field-center__stats app-hub-stats">
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat">
                                    <div class="app-hub-stat__label">订单总数</div>
                                    <div class="app-hub-stat__value">{{ stats.order_count }}</div>
                                </div>
                            </el-col>
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat app-hub-stat--muted">
                                    <div class="app-hub-stat__label">{{ fieldAmountPayableSumLabel }}</div>
                                    <div class="app-hub-stat__value">¥ {{ formatMoney(stats.field_payable_sum) }}</div>
                                </div>
                            </el-col>
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat app-hub-stat--muted">
                                    <div class="app-hub-stat__label">{{ fieldAmountPaidSumLabel }}</div>
                                    <div class="app-hub-stat__value">¥ {{ formatMoney(stats.field_paid_sum) }}</div>
                                </div>
                            </el-col>
                            <el-col :xs="12" :sm="6">
                                <div class="app-hub-stat app-hub-stat--accent">
                                    <div class="app-hub-stat__label">{{ fieldAmountPendingSumLabel }}</div>
                                    <div class="app-hub-stat__value">¥ {{ formatMoney(stats.field_pending_sum) }}</div>
                                </div>
                            </el-col>
                        </el-row>

                        <el-form :inline="true" size="small" class="field-center__filters app-hub-filters" @submit.native.prevent="runSearch">
                            <div class="field-center__filters-left app-hub-filters__left">
                                <el-form-item label="综合">
                                    <el-input
                                        v-model.trim="filters.keyword"
                                        clearable
                                        placeholder="公司名称、信用机构代码证搜索"
                                        class="field-center__filter-input--wide app-search-input"
                                        @keyup.enter.native="runSearch"
                                    />
                                </el-form-item>
                                <el-form-item v-if="!isFieldSubaccount" label="合作外勤">
                                    <el-select
                                        v-model="filters.field_partner_id"
                                        clearable
                                        filterable
                                        placeholder="全部"
                                        class="field-center__filter-select"
                                    >
                                        <el-option
                                            v-for="p in partnerOptions"
                                            :key="p.id"
                                            :label="partnerOptionLabel(p)"
                                            :value="p.id"
                                        />
                                    </el-select>
                                </el-form-item>
                                <el-form-item v-if="isFieldSubaccount" label="结算">
                                    <el-select
                                        v-model="filters.settlement_status"
                                        clearable
                                        placeholder="全部"
                                        class="field-center__filter-select--narrow"
                                    >
                                        <el-option label="未结清" value="unsettled" />
                                        <el-option label="已结清" value="settled" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item v-else-if="isSuperAdminUser" label="结算">
                                    <el-select
                                        v-model="filters.settlement_status"
                                        clearable
                                        placeholder="全部"
                                        class="field-center__filter-select--narrow"
                                    >
                                        <el-option label="已结清" value="settled" />
                                        <el-option label="未结清" value="unsettled" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item v-else label="归档模块">
                                    <el-select
                                        v-model="filters.is_archived"
                                        clearable
                                        placeholder="全部"
                                        class="field-center__filter-select--narrow"
                                    >
                                        <el-option label="未归档" :value="0" />
                                        <el-option label="已归档" :value="1" />
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="field-center__filters-right app-hub-filters__right">
                                <el-form-item class="field-center__filters-actions app-hub-filters__actions">
                                    <el-button type="primary" @click="runSearch">查询</el-button>
                                    <el-button @click="resetFilters">重置</el-button>
                                </el-form-item>
                            </div>
                        </el-form>

                        <el-table
                            v-loading="ordersLoading"
                            :data="orders"
                            border
                            stripe
                            size="small"
                            class="field-center__table app-hub-table"
                            :header-cell-style="{ textAlign: 'center' }"
                            :cell-style="{ textAlign: 'center' }"
                        >
                            <el-table-column label="日期" width="85" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatOrderDate(row) }}</template>
                            </el-table-column>
                            <el-table-column label="公司名称" width="250" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ row.company && row.company.name ? row.company.name : '—' }}</template>
                            </el-table-column>
                            <el-table-column label="统一信用代码" width="160" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ row.company && row.company.credit_code ? row.company.credit_code : '—' }}</template>
                            </el-table-column>
                            <el-table-column label="合作外勤" width="90" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ fieldPartnerName(row) }}</template>
                            </el-table-column>
                            <el-table-column label="外勤手机号" width="105" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ fieldPartnerPhone(row) }}</template>
                            </el-table-column>
                            <el-table-column label="税务进度" width="90" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatTaxProgressLabel(row) }}</template>
                            </el-table-column>
                            <el-table-column label="工商进度" width="90" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">{{ formatBizProgressLabel(row) }}</template>
                            </el-table-column>
                            <el-table-column :label="fieldAmountPayableColLabel" width="90" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">¥ {{ fieldFee(row, 'payable') }}</template>
                            </el-table-column>
                            <el-table-column :label="fieldAmountPaidColLabel" width="90" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">¥ {{ fieldFee(row, 'paid') }}</template>
                            </el-table-column>
                            <el-table-column :label="fieldAmountPendingColLabel" width="90" align="center" header-align="center" show-overflow-tooltip>
                                <template slot-scope="{ row }">¥ {{ fieldFee(row, 'pending') }}</template>
                            </el-table-column>
                            <el-table-column v-if="isFieldSubaccount" label="操作" width="120" align="center" header-align="center">
                                <template slot-scope="{ row }">
                                    <el-button
                                        type="text"
                                        size="small"
                                        :class="fieldConfirmActionClass(row)"
                                        :disabled="!canConfirmCollection(row)"
                                        @click="confirmCollection(row)"
                                    >
                                        确认收款
                                    </el-button>
                                    <el-button
                                        type="text"
                                        size="small"
                                        :disabled="!canRemindCollection(row)"
                                        @click="remindCollection(row)"
                                    >
                                        催款
                                    </el-button>
                                </template>
                            </el-table-column>
                            <el-table-column v-else label="操作" width="130" align="center" header-align="center">
                                <template slot-scope="{ row }">
                                    <el-button
                                        type="text"
                                        size="small"
                                        :class="fieldFeeActionClass(row)"
                                        :disabled="isFieldFeeLocked(row)"
                                        @click="openFeeDialog(row)"
                                    >
                                        费用登记
                                    </el-button>
                                    <el-button
                                        v-if="canDeleteFieldCenterOrder"
                                        type="text"
                                        size="small"
                                        class="field-center__delete"
                                        @click="confirmDeleteOrder(row)"
                                    >
                                        删除
                                    </el-button>
                                </template>
                            </el-table-column>
                        </el-table>

                        <div v-if="meta.total > 0" class="field-center__pager app-hub-pager app-pagination-antd-wrap">
                            <el-pagination
                                background
                                layout="total, prev, pager, next, sizes"
                                popper-class="app-pagination-antd-sizes-popper"
                                :page-sizes="[10, 20, 50, 100]"
                                :current-page="page"
                                :page-size="perPage"
                                :total="meta.total"
                                @current-change="handlePageChange"
                                @size-change="handlePageSizeChange"
                            />
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane v-if="showPartnersManageTab" label="外勤管理" name="partners" lazy>
                    <div class="field-center__pane app-hub-pane">
                        <settings-field-partners />
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-card>

        <el-dialog
            title="外勤费用登记"
            :visible.sync="feeDialogVisible"
            width="480px"
            append-to-body
            @closed="resetFeeForm"
        >
            <el-form label-width="100px" size="small">
                <el-form-item label="应付外勤">
                    <el-input-number
                        v-model="feeForm.field_payable_total"
                        :min="0"
                        :precision="2"
                        :step="100"
                        :controls="false"
                        class="field-center__fee-input"
                    />
                </el-form-item>
                <el-form-item label="已付外勤">
                    <el-input-number
                        v-model="feeForm.field_paid_total"
                        :min="0"
                        :precision="2"
                        :step="100"
                        :controls="false"
                        class="field-center__fee-input"
                    />
                </el-form-item>
                <el-form-item label="待付外勤">
                    <el-input
                        :value="feePendingDisplay"
                        disabled
                        class="field-center__fee-readonly field-center__fee-pending"
                    />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button type="primary" :loading="feeSaving" @click="submitFees">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import SettingsFieldPartners from './SettingsFieldPartners.vue';
import { FIELD_PARTNERS_MODULE_TITLE } from '../constants/nav-labels';

export default {
    name: 'FieldPartnersPage',
    components: {
        SettingsFieldPartners: SettingsFieldPartners
    },
    data: function () {
        return {
            pageTitle: FIELD_PARTNERS_MODULE_TITLE,
            activeTab: 'orders',
            stats: {
                order_count: 0,
                field_payable_sum: 0,
                field_paid_sum: 0,
                field_pending_sum: 0
            },
            orders: [],
            meta: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0
            },
            page: 1,
            perPage: 10,
            ordersLoading: false,
            filters: {
                keyword: '',
                field_partner_id: '',
                is_archived: '',
                settlement_status: ''
            },
            partnerOptions: [],
            feeDialogVisible: false,
            feeSaving: false,
            actionSubmittingId: null,
            feeForm: {
                order_id: null,
                order_no: '',
                company_name: '',
                partner_name: '',
                field_payable_total: 0,
                field_paid_total: 0
            }
        };
    },
    watch: {
        activeTab: function (name) {
            if (name === 'orders') {
                this.fetchPartnerOptions();
            }
        }
    },
    computed: {
        showPartnersManageTab: function () {
            var u = this.$store.getters.currentUser;
            var perms = u && u.permissions ? u.permissions : [];
            var roles = u && u.roles ? u.roles : [];
            return perms.indexOf('field_partner.manage') >= 0 || perms.indexOf('order.edit') >= 0 || roles.indexOf('field-specialist') >= 0;
        },
        canDeleteFieldCenterOrder: function () {
            var u = this.$store.getters.currentUser;
            var perms = u && u.permissions ? u.permissions : [];
            return perms.indexOf('order.delete') >= 0 || perms.indexOf('order.edit') >= 0;
        },
        isFieldSubaccount: function () {
            var u = this.$store.getters.currentUser;
            return !!(u && !u.is_super_admin);
        },
        isSuperAdminUser: function () {
            var u = this.$store.getters.currentUser;
            return !!(u && u.is_super_admin);
        },
        fieldAmountPayableSumLabel: function () {
            return this.isFieldSubaccount ? '应收合计' : '应付合计';
        },
        fieldAmountPaidSumLabel: function () {
            return this.isFieldSubaccount ? '已收合计' : '已付合计';
        },
        fieldAmountPendingSumLabel: function () {
            return this.isFieldSubaccount ? '待收合计' : '待付合计';
        },
        fieldAmountPayableColLabel: function () {
            return this.isFieldSubaccount ? '应收' : '应付';
        },
        fieldAmountPaidColLabel: function () {
            return this.isFieldSubaccount ? '已收' : '已付';
        },
        fieldAmountPendingColLabel: function () {
            return this.isFieldSubaccount ? '待收' : '待付';
        },
        feePendingDisplay: function () {
            var a = Number(this.feeForm.field_payable_total || 0);
            var b = Number(this.feeForm.field_paid_total || 0);
            var p = Math.max(0, a - b);
            return this.formatMoney(p);
        }
    },
    created: function () {
        if (!this.showPartnersManageTab && this.activeTab === 'partners') {
            this.activeTab = 'orders';
        }
        this.fetchPartnerOptions();
        this.fetchOrders();
    },
    methods: {
        formatMoney: function (v) {
            var n = Number(v);
            if (isNaN(n)) {
                return '0.00';
            }
            return n.toFixed(2);
        },
        partnerOptionLabel: function (p) {
            if (!p) {
                return '';
            }
            var name = p.name ? String(p.name) : ('ID ' + p.id);
            var province = p.province ? String(p.province) : '';
            return province ? (name + '-' + province) : name;
        },
        formatOrderDate: function (row) {
            if (!row) {
                return '—';
            }
            var d = row.receipt_date;
            if (d && typeof d === 'string') {
                return d.length >= 10 ? d.slice(0, 10) : d;
            }
            if (row.created_at) {
                var s = String(row.created_at);
                return s.length >= 10 ? s.slice(0, 10) : s;
            }
            return '—';
        },
        fetchPartnerOptions: function () {
            var _this = this;
            window.axios.get('/field-partners', {
                params: {
                    active_only: 1,
                    specialist_only: 1
                }
            }).then(function (res) {
                _this.partnerOptions = res.data.data || [];
            }).catch(function () {
                _this.partnerOptions = [];
            });
        },
        buildOrderQueryParams: function () {
            var p = {
                page: this.page,
                per_page: this.perPage
            };
            if (this.filters.keyword) {
                p.keyword = this.filters.keyword;
            }
            if (!this.isFieldSubaccount && this.filters.field_partner_id !== '' && this.filters.field_partner_id != null) {
                p.field_partner_id = this.filters.field_partner_id;
            }
            if (this.filters.is_archived !== '' && this.filters.is_archived !== null) {
                p.is_archived = this.filters.is_archived;
            }
            if ((this.isSuperAdminUser || this.isFieldSubaccount) && this.filters.settlement_status) {
                p.settlement_status = this.filters.settlement_status;
                delete p.is_archived;
            }
            return p;
        },
        fetchOrders: function () {
            var _this = this;
            this.ordersLoading = true;
            window.axios.get('/field-center/orders', { params: this.buildOrderQueryParams() }).then(function (res) {
                _this.orders = res.data.data || [];
                _this.meta = res.data.meta || _this.meta;
                if (res.data.stats) {
                    _this.stats = res.data.stats;
                }
            }).catch(function () {
                _this.$message.error('加载外勤订单失败');
            }).finally(function () {
                _this.ordersLoading = false;
            });
        },
        runSearch: function () {
            this.page = 1;
            this.fetchOrders();
        },
        resetFilters: function () {
            this.filters = {
                keyword: '',
                field_partner_id: '',
                is_archived: '',
                settlement_status: ''
            };
            this.page = 1;
            this.fetchOrders();
        },
        handlePageChange: function (p) {
            this.page = p;
            this.fetchOrders();
        },
        handlePageSizeChange: function (size) {
            this.perPage = size;
            this.page = 1;
            this.fetchOrders();
        },
        fieldPartnerName: function (row) {
            if (row.work && row.work.field_partner) {
                return row.work.field_partner.name || '—';
            }
            return '—';
        },
        fieldPartnerPhone: function (row) {
            if (row.work && row.work.field_partner) {
                return row.work.field_partner.phone || '—';
            }
            return '—';
        },
        formatTaxProgressLabel: function (row) {
            var n = row && row.tax_progress_status != null ? Number(row.tax_progress_status) : null;
            if (n === null || Number.isNaN(n)) {
                return '—';
            }
            var map = {
                1: '税务未开',
                3: '清税中',
                4: '已清税'
            };
            return map[n] || '—';
        },
        /** 与渠道中心订单列表工商进度口径一致 */
        formatBizProgressLabel: function (row) {
            if (!row) {
                return '—';
            }
            if (Number(row.biz_progress_status || 0) === 104) {
                return '已驳回';
            }
            // 外勤订单页：税务未清税前，工商进度统一不展示。
            if (Number(row.tax_progress_status || 0) !== 4) {
                return '—';
            }
            if (this.isOrderExternalRegDeregistered(row.external_reg_status)) {
                return '已注销';
            }
            if (Number(row.is_archived) === 1) {
                return '已注销';
            }
            var announcement = Number(row.biz_announcement_status || 0);
            var annual = Number(row.annual_report_stage || 0);
            var license = Number(row.biz_license_status || 0);
            var progress = Number(row.biz_progress_status || 0);
            var uninitialized = announcement === 0 && annual === 0 && license === 9 && progress === 101;
            if (uninitialized) {
                return '—';
            }
            var n = row.biz_progress_status != null ? Number(row.biz_progress_status) : null;
            if (n === null || Number.isNaN(n)) {
                return '—';
            }
            var map = {
                101: '办理中',
                102: '公示中',
                103: '待提交',
                104: '异常'
            };
            return map[n] || '—';
        },
        isOrderExternalRegDeregistered: function (statusText) {
            if (statusText === null || statusText === undefined) {
                return false;
            }
            return String(statusText).indexOf('注销') !== -1;
        },
        fieldFee: function (row, part) {
            var f = row.financial;
            if (!f) {
                return this.formatMoney(0);
            }
            if (part === 'payable') {
                return this.formatMoney(f.field_payable_total);
            }
            if (part === 'paid') {
                return this.formatMoney(f.field_paid_total);
            }
            if (f.field_pending_total != null) {
                return this.formatMoney(f.field_pending_total);
            }
            var a = Number(f.field_payable_total || 0);
            var b = Number(f.field_paid_total || 0);
            return this.formatMoney(Math.max(0, a - b));
        },
        fieldFeePending: function (row) {
            var f = row && row.financial ? row.financial : null;
            if (!f) {
                return 0;
            }
            if (f.field_pending_total != null) {
                return Number(f.field_pending_total);
            }
            var a = Number(f.field_payable_total || 0);
            var b = Number(f.field_paid_total || 0);
            return Math.max(0, a - b);
        },
        isFieldFeeLocked: function (row) {
            var u = this.$store.getters.currentUser;
            if (!u || !u.is_super_admin) {
                return false;
            }
            var f = row && row.financial ? row.financial : null;
            if (!f) {
                return false;
            }
            var payable = Number(f.field_payable_total || 0);
            return payable !== 0 && this.fieldFeePending(row) <= 0;
        },
        fieldFeeActionClass: function (row) {
            return this.isFieldFeeLocked(row) ? 'field-center__fee-action-locked' : '';
        },
        isFieldReceiptConfirmed: function (row) {
            var f = row && row.financial ? row.financial : null;
            return !!(f && f.field_receipt_confirmed_at);
        },
        isFieldConfirmSettled: function (row) {
            var f = row && row.financial ? row.financial : null;
            if (!f) {
                return false;
            }
            var payable = Number(f.field_payable_total || 0);
            return payable !== 0 && this.fieldFeePending(row) <= 0 && this.isFieldReceiptConfirmed(row);
        },
        fieldConfirmActionClass: function (row) {
            return this.isFieldConfirmSettled(row) ? 'field-center__confirm-action-done' : '';
        },
        isOrderDeregistered: function (row) {
            if (!row) {
                return false;
            }
            if (Number(row.is_archived) === 1) {
                return true;
            }
            if (this.isOrderExternalRegDeregistered(row.external_reg_status)) {
                return true;
            }
            return Number(row.biz_progress_status || 0) === 103;
        },
        canConfirmCollection: function (row) {
            if (!this.isFieldSubaccount || !row) {
                return false;
            }
            if (this.isFieldReceiptConfirmed(row)) {
                return false;
            }
            if (Number(row.is_archived) === 1) {
                return false;
            }
            var f = row && row.financial ? row.financial : null;
            var payable = Number(f && f.field_payable_total != null ? f.field_payable_total : 0);
            if (payable === 0) {
                return false;
            }
            return this.fieldFeePending(row) <= 0;
        },
        canRemindCollection: function (row) {
            if (!this.isFieldSubaccount || !row) {
                return false;
            }
            if (this.isFieldReceiptConfirmed(row)) {
                return false;
            }
            if (this.fieldFeePending(row) <= 0) {
                return false;
            }
            if (Number(row.tax_progress_status || 0) !== 4) {
                return false;
            }
            if (!this.isOrderDeregistered(row)) {
                return false;
            }
            var base = row.external_reg_checked_at || row.updated_at || row.created_at;
            if (!base) {
                return false;
            }
            var ms = new Date(base).getTime();
            if (!Number.isFinite(ms)) {
                return false;
            }
            return (Date.now() - ms) >= (3 * 24 * 60 * 60 * 1000);
        },
        confirmCollection: function (row) {
            var _this = this;
            if (!row || !row.id || !this.canConfirmCollection(row) || this.actionSubmittingId) {
                return;
            }
            this.actionSubmittingId = row.id;
            window.axios.post('/orders/' + row.id + '/field-confirm-receipt').then(function (res) {
                _this.$message.success((res.data && res.data.message) || '确认收款成功');
                _this.fetchOrders();
            }).catch(function (err) {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '确认收款失败';
                _this.$message.error(msg);
            }).finally(function () {
                _this.actionSubmittingId = null;
            });
        },
        remindCollection: function (row) {
            var _this = this;
            if (!row || !row.id || !this.canRemindCollection(row) || this.actionSubmittingId) {
                return;
            }
            this.actionSubmittingId = row.id;
            window.axios.post('/orders/' + row.id + '/field-remind-collection').then(function (res) {
                _this.$message.success((res.data && res.data.message) || '催款提醒已发送');
                _this.fetchOrders();
            }).catch(function (err) {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '催款失败';
                _this.$message.error(msg);
            }).finally(function () {
                _this.actionSubmittingId = null;
            });
        },
        openFeeDialog: function (row) {
            if (!row || !row.id) {
                return;
            }
            var f = row.financial || {};
            this.feeForm = {
                order_id: row.id,
                order_no: row.order_no || '',
                company_name: row.company && row.company.name ? row.company.name : '',
                partner_name: this.fieldPartnerName(row),
                field_payable_total: f.field_payable_total != null ? Number(f.field_payable_total) : 0,
                field_paid_total: f.field_paid_total != null ? Number(f.field_paid_total) : 0
            };
            this.feeDialogVisible = true;
        },
        resetFeeForm: function () {
            this.feeForm = {
                order_id: null,
                order_no: '',
                company_name: '',
                partner_name: '',
                field_payable_total: 0,
                field_paid_total: 0
            };
        },
        submitFees: function () {
            var _this = this;
            if (!this.feeForm.order_id) {
                return;
            }
            var payable = Number(this.feeForm.field_payable_total || 0);
            var paid = Number(this.feeForm.field_paid_total || 0);
            if (paid > payable) {
                this.$message.warning('已付外勤不能大于应付外勤');
                return;
            }
            this.feeSaving = true;
            window.axios.put('/orders/' + this.feeForm.order_id + '/field-fees', {
                field_payable_total: payable,
                field_paid_total: paid
            }).then(function () {
                _this.$message.success('外勤费用已保存');
                _this.feeDialogVisible = false;
                _this.fetchOrders();
                _this.fetchPartnerOptions();
            }).catch(function (err) {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '保存失败';
                _this.$message.error(msg);
            }).finally(function () {
                _this.feeSaving = false;
            });
        },
        confirmDeleteOrder: function (row) {
            var _this = this;
            if (!row || !row.id) {
                return;
            }
            this.$confirm('删除后订单将不再显示，是否继续？', '确认删除', {
                type: 'warning'
            }).then(function () {
                return window.axios.delete('/orders/' + row.id);
            }).then(function () {
                _this.$message.success('删除成功');
                _this.fetchOrders();
                _this.fetchPartnerOptions();
            }).catch(function (err) {
                if (err === 'cancel') {
                    return;
                }
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '删除失败';
                _this.$message.error(msg);
            });
        }
    }
};
</script>

<style scoped>
.field-center__tabs /deep/ .el-tabs__header {
    display: none;
}

.field-center__delete {
    color: #f56c6c;
}

.field-center__filter-input--wide {
    width: 260px;
}

.field-center__filter-select {
    width: 200px;
}

.field-center__filter-select--narrow {
    width: 120px;
}

.field-center__fee-action-locked.el-button--text,
.field-center__fee-action-locked.el-button--text.is-disabled {
    color: #67c23a !important;
    -webkit-text-fill-color: #67c23a;
    cursor: not-allowed !important;
}

.field-center__confirm-action-done.el-button--text,
.field-center__confirm-action-done.el-button--text.is-disabled {
    color: #67c23a !important;
    -webkit-text-fill-color: #67c23a;
    cursor: not-allowed !important;
}

.field-center__fee-input {
    width: 100%;
}

.field-center__fee-readonly >>> .el-input__inner {
    text-align: center;
    font-weight: 500;
}

.field-center__fee-pending.field-center__fee-readonly.is-disabled >>> .el-input__inner {
    color: #909399;
    -webkit-text-fill-color: #909399;
    cursor: not-allowed;
}

/* 不按满屏强拉表宽，避免出现「待付」与「操作」之间的空白列（原 fixed=right + width:100% 常见现象） */
.field-center__table.app-hub-table {
    width: auto;
    max-width: 100%;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/views/FieldPartnersPage.vue